<script setup lang="ts">
import {
  ChevronDown as ChevronDownIcon,
  ChevronUp as ChevronUpIcon,
  Crosshair as CrosshairIcon,
  Layers as LayersIcon,
  MoreHorizontal as MoreHorizontalIcon,
  Pause as PauseIcon,
  Play as PlayIcon,
  Radio as RadioIcon,
  Square as SquareIcon,
} from '@lucide/vue'
import { computed, ref } from 'vue'

import { logService, LOG_CATEGORIES } from '@/services/logService'
import { useSafeAreaInsets } from '@/services/safeAreaService'
import { useNavigationStore } from '@/stores/navigationStore'
import { useRecordingStore } from '@/stores/recordingStore'

const emit = defineEmits<{
  end: []
  recenter: []
  layers: []
}>()

const navigationStore = useNavigationStore()
const recordingStore = useRecordingStore()
const safeAreaInsets = useSafeAreaInsets()
const isStatsCollapsed = ref(false)

const formatSpeed = (speedMps: number | null | undefined) => {
  if (typeof speedMps !== 'number' || !Number.isFinite(speedMps)) return '--'
  return `${Math.max(0, speedMps * 3.6).toFixed(1)}`
}

const formatElevation = (elevationM: number | null | undefined) => {
  if (typeof elevationM !== 'number' || !Number.isFinite(elevationM)) return '--'
  return `${Math.round(elevationM)}`
}

const formatDistance = (distanceM: number | null | undefined) => {
  if (typeof distanceM !== 'number' || !Number.isFinite(distanceM)) return '--'
  if (distanceM >= 1000) return `${(distanceM / 1000).toFixed(1)} km`
  return `${Math.round(distanceM)} m`
}

const location = computed(() => navigationStore.currentLocation)
const targetName = computed(() => navigationStore.target?.name ?? 'Navigation')
const speedText = computed(() => formatSpeed(location.value?.speedMps))
const elevationText = computed(() => formatElevation(location.value?.elevationM))
const remainingText = computed(() => formatDistance(navigationStore.remainingDistanceM))
const offRouteText = computed(() => formatDistance(navigationStore.offRouteDistanceM))
const recordingActionLabel = computed(() => {
  if (!recordingStore.isSupported) return 'Recording unavailable'
  if (recordingStore.isActive) return 'Pause recording'
  if (recordingStore.isPaused) return 'Resume recording'
  if (recordingStore.hasStoppedDraft) return 'Recording stopped'
  return 'Start recording'
})

const handleRecordingAction = async () => {
  if (!recordingStore.isSupported || recordingStore.isBusy || recordingStore.hasStoppedDraft) return

  try {
    if (recordingStore.isActive) {
      await recordingStore.pause()
    } else if (recordingStore.isPaused) {
      await recordingStore.resume()
    } else {
      await recordingStore.start()
    }
  } catch (error) {
    logService.error(LOG_CATEGORIES.GENERAL, 'Failed to update recording from navigation HUD', {
      error,
    })
  }
}
</script>

<template>
  <div class="pointer-events-none fixed inset-0 z-[95]">
    <div
      class="pointer-events-auto absolute inset-x-3 rounded-3xl bg-black/45 px-4 py-3 text-white shadow-xl backdrop-blur-md"
      :style="{ top: `calc(${safeAreaInsets.top} + 12px)` }"
      data-testid="mobile-navigation-hud-top"
    >
      <div
        class="flex min-w-0 items-center justify-between gap-3"
        :class="{ 'mb-2': !isStatsCollapsed }"
      >
        <p class="truncate text-sm font-semibold">{{ targetName }}</p>
        <div class="flex shrink-0 items-center gap-2">
          <p v-if="navigationStore.locationError" class="text-xs text-amber-100">
            Location pending
          </p>
          <button
            type="button"
            class="flex h-8 w-8 items-center justify-center rounded-full bg-white/12 text-white transition-colors active:bg-white/20"
            :aria-label="isStatsCollapsed ? 'Expand navigation stats' : 'Minimize navigation stats'"
            @click="isStatsCollapsed = !isStatsCollapsed"
          >
            <component :is="isStatsCollapsed ? ChevronDownIcon : ChevronUpIcon" class="h-4 w-4" />
          </button>
        </div>
      </div>
      <div
        v-if="isStatsCollapsed"
        class="mt-1 flex items-center justify-between gap-4 text-sm font-semibold"
      >
        <span>{{ speedText }} km/h</span>
        <span>{{ remainingText }}</span>
        <span>{{ offRouteText }}</span>
      </div>
      <div v-else class="grid grid-cols-2 gap-x-4 gap-y-2">
        <div>
          <p class="text-[11px] font-medium uppercase text-white/70">Speed</p>
          <p class="text-2xl font-semibold leading-none">
            {{ speedText }} <span class="text-sm">km/h</span>
          </p>
        </div>
        <div class="text-right">
          <p class="text-[11px] font-medium uppercase text-white/70">Elevation</p>
          <p class="text-2xl font-semibold leading-none">
            {{ elevationText }} <span class="text-sm">m</span>
          </p>
        </div>
        <div>
          <p class="text-[11px] font-medium uppercase text-white/70">Remaining</p>
          <p class="text-base font-semibold">{{ remainingText }}</p>
        </div>
        <div class="text-right">
          <p class="text-[11px] font-medium uppercase text-white/70">Off route</p>
          <p class="text-base font-semibold">{{ offRouteText }}</p>
        </div>
      </div>
    </div>

    <div
      class="pointer-events-auto absolute inset-x-3 flex items-center gap-2 rounded-3xl bg-white/92 px-3 py-2 shadow-2xl ring-1 ring-black/10 backdrop-blur-md"
      :style="{ bottom: `calc(${safeAreaInsets.bottom} + 12px)` }"
      data-testid="mobile-navigation-hud-bottom"
    >
      <button
        type="button"
        class="flex h-12 min-w-[76px] items-center justify-center rounded-2xl bg-gray-900 px-4 text-sm font-semibold text-white"
        aria-label="End navigation"
        @click="emit('end')"
      >
        End
      </button>

      <button
        type="button"
        class="flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl bg-emerald-700 px-4 text-sm font-semibold text-white disabled:opacity-50"
        :disabled="
          recordingStore.isBusy || !recordingStore.isSupported || recordingStore.hasStoppedDraft
        "
        :aria-label="recordingActionLabel"
        @click="handleRecordingAction"
      >
        <component
          :is="recordingStore.isActive ? PauseIcon : recordingStore.isPaused ? PlayIcon : RadioIcon"
          class="h-5 w-5"
        />
        <span>{{
          recordingStore.isActive ? 'Pause' : recordingStore.isPaused ? 'Resume' : 'Record'
        }}</span>
      </button>

      <button
        type="button"
        class="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-gray-700 shadow-sm ring-1 ring-gray-200"
        :class="{ 'text-emerald-700 ring-emerald-200': navigationStore.followMap }"
        aria-label="Recenter map"
        @click="emit('recenter')"
      >
        <CrosshairIcon class="h-5 w-5" />
      </button>

      <button
        type="button"
        class="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-gray-700 shadow-sm ring-1 ring-gray-200"
        aria-label="Open map layers"
        @click="emit('layers')"
      >
        <LayersIcon class="h-5 w-5" />
      </button>

      <button
        type="button"
        class="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-gray-700 shadow-sm ring-1 ring-gray-200"
        aria-label="More navigation options"
        @click="recordingStore.openModal"
      >
        <component
          :is="recordingStore.hasRecording ? SquareIcon : MoreHorizontalIcon"
          class="h-5 w-5"
        />
      </button>
    </div>
  </div>
</template>
